import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  IconButton,
  Chip,
  useMediaQuery,
} from '@mui/material'
import { ThemeProvider, alpha } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import LightModeIcon from '@mui/icons-material/LightMode'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import DrawIcon from '@mui/icons-material/Draw'
import AbcIcon from '@mui/icons-material/Abc'
import RefreshIcon from '@mui/icons-material/Refresh'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { useTranslation } from 'react-i18next'
import { createAppTheme } from '../../theme/theme'
import { backendUrl } from '../../config'
import LanguageSelector from '../../components/LanguageSelector'

const GOLD = '#c9a96e'

// ─── API helpers (no auth header needed) ──────────────────────────────────────
const publicApi = async (token, path, options = {}) => {
  const res = await fetch(`${backendUrl}api/sign/${token}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.detail || 'An error occurred.')
  }
  return res.json()
}

// ─── OTP Input component (6 individual boxes) ─────────────────────────────────
const OTPInput = ({ value, onChange, disabled }) => {
  const inputRefs = useRef([])

  const handleChange = (index, val) => {
    if (!/^[0-9]?$/.test(val)) return
    const next = [...value]
    next[index] = val
    onChange(next)
    if (val && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (pasted.length) {
      const next = pasted.split('').concat(Array(6).fill('')).slice(0, 6)
      onChange(next)
      inputRefs.current[Math.min(pasted.length, 5)]?.focus()
    }
  }

  return (
    <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}>
      {value.map((digit, i) => (
        <TextField
          key={i}
          inputRef={(el) => (inputRefs.current[i] = el)}
          value={digit}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          disabled={disabled}
          inputProps={{
            maxLength: 1,
            style: {
              textAlign: 'center',
              fontSize: '1.5rem',
              fontWeight: 700,
              padding: '10px 0',
              fontFamily: 'monospace',
              color: 'inherit',
            },
          }}
          sx={(theme) => ({
            width: 52,
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              color: theme.palette.text.primary,
              '& fieldset': {
                borderColor: digit ? GOLD : (theme.palette.mode === 'light' ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.23)'),
                borderWidth: '1.5px',
              },
              '&:hover fieldset': {
                borderColor: GOLD,
              },
              '&.Mui-focused fieldset': {
                borderColor: `${GOLD} !important`,
                borderWidth: '2px',
              },
            },
          })}
        />
      ))}
    </Box>
  )
}

// ─── Main SigningScreen ────────────────────────────────────────────────────────
const SigningScreen = () => {
  const { token } = useParams()
  const { t } = useTranslation()
  const [themeMode, setThemeMode] = useState('dark')
  const theme = createAppTheme(themeMode)
  const isDark = themeMode === 'dark'
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // Steps: loading | read-contract | sign | enter-otp | done | error
  const [step, setStep] = useState('loading')
  const [pageData, setPageData] = useState(null)
  const [error, setError] = useState(null)

  // Sign step
  const [signTab, setSignTab] = useState(0) // 0=draw, 1=type
  const [typedName, setTypedName] = useState('')
  const [agreed, setAgreed] = useState(false)
  const [isDrawing, setIsDrawing] = useState(false)
  const [canvasHasContent, setCanvasHasContent] = useState(false)
  const canvasRef = useRef(null)
  const lastPos = useRef(null)

  // Saved signature — captured before canvas unmounts when moving to OTP step
  const savedSignature = useRef({ data: '', type: '' })

  // OTP step
  const [otp, setOtp] = useState(['', '', '', '', '', ''])
  const [otpLoading, setOtpLoading] = useState(false)
  const [otpError, setOtpError] = useState(null)
  const [countdown, setCountdown] = useState(0)
  const [submitting, setSubmitting] = useState(false)

  // ── Load page data ──────────────────────────────────────────────────────────
  useEffect(() => {
    publicApi(token, '')
      .then((data) => {
        setPageData(data)
        setStep('read-contract')
      })
      .catch((err) => {
        setError(err.message)
        setStep('error')
      })
  }, [token])

  // ── Canvas setup ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (step !== 'sign' || signTab !== 0) return
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    ctx.strokeStyle = '#1c1917' // Always dark ink on white canvas
    ctx.lineWidth = 2.5
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
  }, [step, signTab, isDark])

  const getCanvasPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect()
    const clientX = e.touches ? e.touches[0].clientX : e.clientX
    const clientY = e.touches ? e.touches[0].clientY : e.clientY
    return { x: clientX - rect.left, y: clientY - rect.top }
  }

  const handleCanvasDown = useCallback((e) => {
    e.preventDefault()
    const canvas = canvasRef.current
    if (!canvas) return
    setIsDrawing(true)
    const pos = getCanvasPos(e, canvas)
    lastPos.current = pos
    const ctx = canvas.getContext('2d')
    ctx.beginPath()
    ctx.moveTo(pos.x, pos.y)
  }, [])

  const handleCanvasMove = useCallback((e) => {
    e.preventDefault()
    if (!isDrawing) return
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    const pos = getCanvasPos(e, canvas)
    ctx.lineTo(pos.x, pos.y)
    ctx.stroke()
    lastPos.current = pos
    setCanvasHasContent(true)
  }, [isDrawing])

  const handleCanvasUp = useCallback(() => setIsDrawing(false), [])

  const clearCanvas = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height)
    setCanvasHasContent(false)
  }

  // ── Countdown timer ─────────────────────────────────────────────────────────
  const startCountdown = (seconds) => {
    setCountdown(seconds)
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) { clearInterval(interval); return 0 }
        return prev - 1
      })
    }, 1000)
  }

  // ── Send OTP (called when user clicks Continuer on sign step) ───────────────
  const handleSendOtp = async () => {
    setOtpLoading(true)
    setOtpError(null)
    try {
      await publicApi(token, '/send-otp', { method: 'POST', body: '{}' })
      setStep('enter-otp')
      setOtp(['', '', '', '', '', ''])
      startCountdown(60)
    } catch (err) {
      setOtpError(err.message)
    } finally {
      setOtpLoading(false)
    }
  }

  // ── On "Continuer" click: capture signature then send OTP ───────────────────
  const handleContinueToOtp = () => {
    if (!agreed) return
    if (signTab === 0 && !canvasHasContent) return
    if (signTab === 1 && !typedName.trim()) return

    // Capture signature data NOW while canvas is still mounted
    if (signTab === 0) {
      const canvas = canvasRef.current
      savedSignature.current = {
        data: canvas ? canvas.toDataURL('image/png').split(',')[1] : '',
        type: 'drawn',
      }
    } else {
      savedSignature.current = { data: typedName.trim(), type: 'typed' }
    }

    handleSendOtp()
  }

  // ── Auto-submit when all 6 digits entered ───────────────────────────────────
  useEffect(() => {
    if (step !== 'enter-otp') return
    const otpValue = otp.join('')
    if (otpValue.length === 6) {
      handleVerifyAndSubmit(otpValue)
    }
  }, [otp, step])

  // ── Verify OTP then submit signature ────────────────────────────────────────
  const handleVerifyAndSubmit = async (otpValue) => {
    setSubmitting(true)
    setOtpError(null)
    try {
      // Step 1: verify OTP
      await publicApi(token, '/verify-otp', {
        method: 'POST',
        body: JSON.stringify({ otp_code: otpValue }),
      })

      // Step 2: use signature data saved before canvas unmounted
      const { data: signatureData, type: signatureType } = savedSignature.current

      // Step 3: submit
      await publicApi(token, '/submit', {
        method: 'POST',
        body: JSON.stringify({ signature_data: signatureData, signature_type: signatureType }),
      })

      setStep('done')
    } catch (err) {
      setOtpError(err.message)
      setOtp(['', '', '', '', '', ''])
    } finally {
      setSubmitting(false)
    }
  }

  const canContinue = agreed && (signTab === 0 ? canvasHasContent : typedName.trim().length > 0)

  // ── Shared layout ────────────────────────────────────────────────────────────
  const bgColor = isDark ? '#0c0a09' : '#f5f5f4'
  const cardBg = isDark ? '#1c1917' : '#ffffff'
  const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'

  // Variant for LanguageSelector: adapt to theme mode
  const langVariant = isDark ? 'dark' : 'light'

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: bgColor,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: { xs: 0, sm: 4 },
          px: { xs: 0, sm: 2 },
        }}
      >
        {/* Top bar */}
        <Box
          sx={{
            width: '100%',
            maxWidth: 720,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: { xs: 2, sm: 0 },
            py: 2,
            mb: { xs: 0, sm: 1 },
          }}
        >
          <Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.25rem',
                letterSpacing: '0.12em',
                color: GOLD,
                lineHeight: 1,
              }}
            >
              LAWIS
            </Typography>
            <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', letterSpacing: '0.2em', textTransform: 'uppercase' }}>
              AI Legal Intelligence
            </Typography>
          </Box>
          {/* Right controls: Language selector + theme toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LanguageSelector variant={langVariant} />
            <IconButton
              onClick={() => setThemeMode((m) => (m === 'dark' ? 'light' : 'dark'))}
              size="small"
              sx={{
                border: `1px solid ${borderColor}`,
                borderRadius: '8px',
                p: 0.75,
                color: 'text.secondary',
              }}
            >
              {isDark ? <LightModeIcon sx={{ fontSize: 18 }} /> : <DarkModeIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </Box>
        </Box>

        {/* Card */}
        <Box
          sx={{
            width: '100%',
            maxWidth: 720,
            backgroundColor: cardBg,
            borderRadius: { xs: 0, sm: '16px' },
            border: { xs: 'none', sm: `1px solid ${borderColor}` },
            overflow: 'hidden',
            flexGrow: { xs: 1, sm: 0 },
          }}
        >
          {/* ── LOADING ── */}
          {step === 'loading' && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
              <CircularProgress sx={{ color: GOLD }} />
            </Box>
          )}

          {/* ── ERROR ── */}
          {step === 'error' && (
            <Box sx={{ p: { xs: 3, sm: 5 }, textAlign: 'center' }}>
              <ErrorOutlineIcon sx={{ fontSize: 52, color: 'error.main', mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                {t('signing.error_title')}
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem', mb: 3 }}>
                {error || t('signing.error_default')}
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                {t('signing.error_contact')}
                <br />
                <a href="mailto:saad.lachhab@lawis.ai" style={{ color: GOLD }}>saad.lachhab@lawis.ai</a>
              </Typography>
            </Box>
          )}

          {/* ── STEP 1: READ CONTRACT ── */}
          {step === 'read-contract' && pageData && (
            <Box sx={{ p: { xs: 2, sm: 4 } }}>
              {/* Header */}
              <Box sx={{ mb: 3 }}>
                <Chip
                  label={t('signing.signer_label', { name: pageData.signer_name || pageData.signer_email })}
                  size="small"
                  sx={{
                    mb: 1.5,
                    backgroundColor: alpha(GOLD, 0.12),
                    color: GOLD,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    border: `1px solid ${alpha(GOLD, 0.3)}`,
                  }}
                />
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3, mb: 0.5 }}>
                  {pageData.contract_title}
                </Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                  {t('signing.read_instructions')}
                </Typography>
              </Box>

              {/* Divider */}
              <Box sx={{ height: '1px', backgroundColor: borderColor, mb: 3 }} />

              {/* Contract content — read-only scrollable */}
              <Box
                sx={{
                  maxHeight: { xs: '50vh', sm: '55vh' },
                  overflowY: 'auto',
                  border: `1px solid ${borderColor}`,
                  borderRadius: '10px',
                  backgroundColor: isDark ? alpha('#fff', 0.02) : alpha('#000', 0.02),
                  '&::-webkit-scrollbar': { width: '4px' },
                  '&::-webkit-scrollbar-thumb': { background: alpha(GOLD, 0.3), borderRadius: '4px' },
                }}
              >
                <Box
                  sx={{
                    p: { xs: 2, sm: 3 },
                    pointerEvents: 'none',
                    userSelect: 'none',
                    // HTML content typography
                    '& h1': { fontSize: '1.35rem', fontWeight: 700, mt: 2, mb: 1, lineHeight: 1.3 },
                    '& h2': { fontSize: '1.15rem', fontWeight: 700, mt: 2, mb: 0.75, lineHeight: 1.3 },
                    '& h3': { fontSize: '1rem', fontWeight: 600, mt: 1.5, mb: 0.5 },
                    '& p': { mb: 1.25, lineHeight: 1.75, fontSize: '0.9rem', color: 'inherit' },
                    '& strong, & b': { fontWeight: 700 },
                    '& em, & i': { fontStyle: 'italic' },
                    '& u': { textDecoration: 'underline' },
                    '& ul, & ol': { pl: 3, mb: 1.25 },
                    '& li': { mb: 0.5, lineHeight: 1.7, fontSize: '0.9rem' },
                    '& table': { width: '100%', borderCollapse: 'collapse', mb: 2, fontSize: '0.85rem' },
                    '& th': { border: `1px solid ${borderColor}`, p: '8px', fontWeight: 700, textAlign: 'left', backgroundColor: isDark ? alpha('#fff', 0.04) : alpha('#000', 0.04) },
                    '& td': { border: `1px solid ${borderColor}`, p: '8px' },
                    '& blockquote': { borderLeft: `3px solid ${GOLD}`, pl: 2, ml: 0, fontStyle: 'italic', color: 'text.secondary' },
                    '& hr': { border: 'none', borderTop: `1px solid ${borderColor}`, my: 2 },
                    '& a': { color: GOLD, textDecoration: 'underline' },
                    '& *': { fontFamily: 'inherit !important', color: 'inherit', backgroundColor: 'transparent !important', boxSizing: 'border-box' },
                  }}
                  dangerouslySetInnerHTML={{ __html: pageData.contract_content || '<p>Contract content not available.</p>' }}
                />
              </Box>

              <Button
                variant="contained"
                fullWidth
                onClick={() => setStep('sign')}
                sx={{
                  mt: 3,
                  height: 48,
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  borderRadius: '10px',
                  backgroundColor: GOLD,
                  color: '#1c1917',
                  boxShadow: 'none',
                  '&:hover': { backgroundColor: alpha(GOLD, 0.88), boxShadow: 'none' },
                }}
              >
                {t('signing.sign_button')}
              </Button>
            </Box>
          )}

          {/* ── STEP 2: SIGN ── */}
          {step === 'sign' && pageData && (
            <Box sx={{ p: { xs: 2, sm: 4 } }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                {t('signing.sign_title')}
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem', mb: 3 }}>
                {pageData.contract_title}
              </Typography>

              {/* Signature tabs */}
              <Tabs
                value={signTab}
                onChange={(_, v) => { setSignTab(v); clearCanvas(); setTypedName('') }}
                sx={{
                  mb: 2,
                  '& .MuiTab-root': { fontWeight: 600, fontSize: '0.85rem', textTransform: 'none', minWidth: 0 },
                  '& .MuiTabs-indicator': { backgroundColor: GOLD },
                  '& .Mui-selected': { color: `${GOLD} !important` },
                }}
              >
                <Tab icon={<DrawIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={t('signing.tab_draw')} />
                <Tab icon={<AbcIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={t('signing.tab_type')} />
              </Tabs>

              {/* Draw tab */}
              {signTab === 0 && (
                <Box>
                  <Box
                    sx={{
                      border: `2px dashed ${canvasHasContent ? GOLD : borderColor}`,
                      borderRadius: '10px',
                      overflow: 'hidden',
                      position: 'relative',
                      backgroundColor: isDark ? alpha('#fff', 0.03) : '#fafafa',
                      transition: 'border-color 0.2s',
                    }}
                  >
                    <canvas
                      ref={canvasRef}
                      width={isMobile ? 320 : 640}
                      height={130}
                      style={{ display: 'block', width: '100%', height: 130, cursor: 'crosshair', touchAction: 'none' }}
                      onMouseDown={handleCanvasDown}
                      onMouseMove={handleCanvasMove}
                      onMouseUp={handleCanvasUp}
                      onMouseLeave={handleCanvasUp}
                      onTouchStart={handleCanvasDown}
                      onTouchMove={handleCanvasMove}
                      onTouchEnd={handleCanvasUp}
                    />
                    {!canvasHasContent && (
                      <Box
                        sx={{
                          position: 'absolute', inset: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          pointerEvents: 'none',
                        }}
                      >
                        <Typography sx={{ color: 'text.disabled', fontSize: '0.85rem' }}>
                          {t('signing.draw_placeholder')}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  {canvasHasContent && (
                    <Button
                      size="small"
                      startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
                      onClick={clearCanvas}
                      sx={{ mt: 1, color: 'text.secondary', textTransform: 'none', fontSize: '0.78rem' }}
                    >
                      {t('signing.clear')}
                    </Button>
                  )}
                </Box>
              )}

              {/* Type tab */}
              {signTab === 1 && (
                <Box>
                  <TextField
                    fullWidth
                    placeholder={t('signing.type_placeholder')}
                    value={typedName}
                    onChange={(e) => setTypedName(e.target.value)}
                    inputProps={{ style: { fontFamily: 'Georgia, serif', fontSize: '1.1rem', fontStyle: 'italic' } }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        '& fieldset': { borderColor: typedName ? GOLD : undefined },
                      },
                    }}
                  />
                  {typedName && (
                    <Box
                      sx={{
                        mt: 1.5,
                        p: 2,
                        border: `1px solid ${borderColor}`,
                        borderRadius: '8px',
                        backgroundColor: isDark ? alpha('#fff', 0.02) : '#fafafa',
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'Georgia, serif',
                          fontSize: '1.4rem',
                          fontStyle: 'italic',
                          color: 'text.primary',
                          borderBottom: `1px solid ${alpha(GOLD, 0.5)}`,
                          pb: 0.5,
                          display: 'inline-block',
                        }}
                      >
                        {typedName}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Agreement checkbox */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    sx={{ color: GOLD, '&.Mui-checked': { color: GOLD }, py: 0 }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
                    {t('signing.agree_checkbox')}
                  </Typography>
                }
                sx={{ mt: 2, alignItems: 'center' }}
              />

              {otpError && (
                <Alert severity="error" sx={{ mt: 2, borderRadius: '8px' }}>{otpError}</Alert>
              )}

              <Button
                variant="contained"
                fullWidth
                disabled={!canContinue || otpLoading}
                onClick={handleContinueToOtp}
                sx={{
                  mt: 2.5,
                  height: 48,
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  borderRadius: '10px',
                  backgroundColor: canContinue ? GOLD : undefined,
                  color: canContinue ? '#1c1917' : undefined,
                  boxShadow: 'none',
                  '&:hover': { backgroundColor: canContinue ? alpha(GOLD, 0.88) : undefined, boxShadow: 'none' },
                  '&:disabled': { opacity: 0.45 },
                }}
              >
                {otpLoading ? <CircularProgress size={20} sx={{ color: '#1c1917' }} /> : t('signing.continue_button')}
              </Button>

              <Button
                variant="text"
                fullWidth
                onClick={() => setStep('read-contract')}
                sx={{ mt: 1, textTransform: 'none', color: 'text.secondary', fontSize: '0.82rem' }}
              >
                {t('signing.back_to_read')}
              </Button>
            </Box>
          )}

          {/* ── STEP 3: ENTER OTP ── */}
          {step === 'enter-otp' && pageData && (
            <Box sx={{ p: { xs: 2, sm: 4 }, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                {t('signing.otp_title')}
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem', mb: 0.5 }}>
                {t('signing.otp_sent_to')}
              </Typography>
              <Typography sx={{ fontWeight: 600, color: GOLD, mb: 3 }}>
                {pageData.signer_email}
              </Typography>

              <OTPInput value={otp} onChange={setOtp} disabled={submitting} />

              {otpError && (
                <Alert severity="error" sx={{ mt: 2, borderRadius: '8px', textAlign: 'left' }}>{otpError}</Alert>
              )}

              {submitting && (
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={18} sx={{ color: GOLD }} />
                  <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>{t('signing.otp_verifying')}</Typography>
                </Box>
              )}

              <Box sx={{ mt: 3 }}>
                {countdown > 0 ? (
                  <Typography sx={{ color: 'text.disabled', fontSize: '0.82rem' }}>
                    {t('signing.otp_resend_countdown', {
                      time: `${Math.floor(countdown / 60)}:${String(countdown % 60).padStart(2, '0')}`,
                    })}
                  </Typography>
                ) : (
                  <Button
                    variant="text"
                    onClick={handleSendOtp}
                    disabled={otpLoading}
                    sx={{ textTransform: 'none', color: GOLD, fontSize: '0.82rem' }}
                  >
                    {otpLoading ? <CircularProgress size={14} sx={{ color: GOLD }} /> : t('signing.otp_resend')}
                  </Button>
                )}
              </Box>

              <Button
                variant="text"
                onClick={() => { setStep('sign'); setOtpError(null) }}
                sx={{ mt: 1, textTransform: 'none', color: 'text.secondary', fontSize: '0.82rem' }}
              >
                {t('signing.otp_back')}
              </Button>
            </Box>
          )}

          {/* ── STEP 4: DONE ── */}
          {step === 'done' && (
            <Box sx={{ p: { xs: 3, sm: 5 }, textAlign: 'center' }}>
              <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                {t('signing.done_title')}
              </Typography>
              {pageData && (
                <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem', mb: 2 }}>
                  {pageData.contract_title}
                </Typography>
              )}
              <Box
                sx={{
                  display: 'inline-block',
                  px: 2,
                  py: 1,
                  borderRadius: '8px',
                  backgroundColor: alpha('#22c55e', 0.1),
                  border: `1px solid ${alpha('#22c55e', 0.25)}`,
                  mb: 3,
                }}
              >
                <Typography sx={{ color: 'success.main', fontSize: '0.82rem', fontWeight: 600 }}>
                  {t('signing.done_badge')}
                </Typography>
              </Box>
              <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                {t('signing.done_message')}
              </Typography>
              <Typography sx={{ color: 'text.disabled', fontSize: '0.78rem', mt: 3 }}>
                <a href="mailto:saad.lachhab@lawis.ai" style={{ color: GOLD, textDecoration: 'none' }}>saad.lachhab@lawis.ai</a>
              </Typography>
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Typography sx={{ color: 'text.disabled', fontSize: '0.72rem', mt: 3, mb: 2 }}>
          {t('signing.footer', { year: new Date().getFullYear() })}
        </Typography>
      </Box>
    </ThemeProvider>
  )
}

export default SigningScreen
